import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { scheduleKeys } from '../../../entities/schedule';
import { useUpdateTechnicianCalendarOrder } from '../../../entities/technician-profile';
import type { OrderItem } from './calendarOrder';

export interface CalendarOrderEditor {
  isEditing: boolean;
  /** `null` until the first change — the widgets then show the server's order. */
  draft: OrderItem[] | null;
  isSaving: boolean;
  isError: boolean;
  /** Something is waiting for the user to decide whether to throw the unsaved order away. */
  isConfirmingDiscard: boolean;
  start: () => void;
  /** Leaves edit mode and drops the draft, no questions asked. */
  cancel: () => void;
  /** "Abbrechen": leaves right away when nothing was changed, otherwise asks first. */
  requestCancel: () => void;
  /**
   * Runs `leave` now when there is nothing unsaved, otherwise asks first and runs it after the user
   * agrees to lose the order. Used for leaving the page.
   */
  guard: (leave: () => void) => void;
  confirmDiscard: () => void;
  dismissDiscard: () => void;
  setDraft: (next: OrderItem[]) => void;
  /** Sends the draft and leaves edit mode once the board has the saved order. */
  save: () => Promise<void>;
}

/**
 * The edit mode of the calendar order. Owned by the page, so the draft survives switching between
 * Tag and Woche; the widgets only read it and report changes.
 */
export function useCalendarOrderEditor(): CalendarOrderEditor {
  const queryClient = useQueryClient();
  const mutation = useUpdateTechnicianCalendarOrder();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<OrderItem[] | null>(null);
  // A function in state: the thing to do once the user has agreed to lose the draft.
  const [pendingLeave, setPendingLeave] = useState<{ run: () => void } | null>(null);

  const start = useCallback(() => {
    mutation.reset();
    setDraft(null);
    setIsEditing(true);
  }, [mutation]);

  const cancel = useCallback(() => {
    setIsEditing(false);
    setDraft(null);
    setPendingLeave(null);
  }, []);

  const guard = useCallback(
    (leave: () => void) => {
      if (draft) setPendingLeave({ run: leave });
      else leave();
    },
    [draft],
  );

  const requestCancel = useCallback(() => guard(cancel), [guard, cancel]);

  const confirmDiscard = useCallback(() => {
    const leave = pendingLeave;
    cancel();
    leave?.run();
  }, [pendingLeave, cancel]);

  const dismissDiscard = useCallback(() => setPendingLeave(null), []);

  const save = useCallback(async () => {
    // Nothing was moved or hidden — there is nothing to send.
    if (!draft) {
      cancel();
      return;
    }

    try {
      await mutation.mutateAsync({
        technicians: draft.map((item) => ({
          technicianId: item.technicianId,
          hidden: item.hidden,
        })),
      });
      // The order is read from the schedule: stay in edit mode until the board has it, so the rows
      // do not snap back to the old order for a moment.
      await queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      cancel();
    } catch {
      // Stays open with the draft intact; `isError` tells the toolbar.
    }
  }, [draft, mutation, queryClient, cancel]);

  return {
    isEditing,
    draft,
    isSaving: mutation.isPending,
    isError: mutation.isError,
    isConfirmingDiscard: pendingLeave !== null,
    start,
    cancel,
    requestCancel,
    guard,
    confirmDiscard,
    dismissDiscard,
    setDraft,
    save,
  };
}
